import { ReactElement, useEffect, useState } from 'react';
import { Box, Button, Code, Divider, Image, Stack, Text } from '@chakra-ui/react';
import { toDataURL } from 'qrcode';

import MfaTotpIcon from '../../../../atoms/mfa-totp-icon';
import { Link } from '../../../../atoms/link';
import { MFAHeader } from '../../MFAHeader';
import { MFAStep } from '../../MFAStep';
import { Title } from '../../../../atoms/Title';

export interface IMfaSetupTotp {
  secretCode?: string;
  onSubmit: () => void;
  onChooseDifferent: () => void;
  totpAppLabel: string;
  onSignOut: () => void;
}

export default function MfaSetupTotp({
  secretCode,
  onSubmit,
  onChooseDifferent,
  totpAppLabel
}: IMfaSetupTotp): ReactElement {
  const [isSetupKey, setIsSetupKey] = useState(false);
  const [qrCode, setQrCode] = useState('');

  const handleChange = () => {
    setIsSetupKey(state => !state);
  };

  useEffect(() => {
    if (secretCode) {
      (async function generateQR() {
        // otpauth://totp/Example:alice@google.com?secret=JBSWY3DPEHPK3PXP
        const txt = `otpauth://totp/${totpAppLabel}?secret=${secretCode}`;
        const res = await toDataURL(txt, {
          errorCorrectionLevel: 'M',
          type: 'image/png'
        });
        setQrCode(res);
      })();
    }
  }, [secretCode, totpAppLabel]);

  const title = 'Use an app to authenticate';

  return (
    <>
      <Title title={title} />

      <MFAHeader icon={<MfaTotpIcon />}>{title}</MFAHeader>

      <MFAStep number={1} title={'Open the authenticator app on your phone'}>
        <Text mb={0}>
          Don't have one?{' '}
          <Link href="https://support.google.com/accounts/answer/1066447" isExternal>
            Try Google Authenticator
          </Link>
        </Text>
      </MFAStep>

      <Divider />

      <MFAStep number={2} title={'Scan this QR code into your authenticator app'}>
        <Box>
          {isSetupKey ? (
            <Code
              fontSize={'2xl'}
              whiteSpace={'normal'}
              display={'block'}
              wordBreak={'break-all'}
              borderRadius={'md'}
              letterSpacing={'widest'}
              my={4}
              px={4}
              py={4}
            >
              {secretCode || ''}
            </Code>
          ) : (
            <Image src={qrCode} alt={`QR Code: ${secretCode}`} />
          )}
        </Box>

        {!isSetupKey ? <Text>Can't scan the code?</Text> : <Text>Try the QR code again?</Text>}

        <Button variant={'secondary'} onClick={handleChange}>
          {isSetupKey ? 'Use a QR instead' : 'Use a setup key instead'}
        </Button>
      </MFAStep>

      <Divider />

      <MFAStep number={3} title={'Enter the 6-digit code from the app'}>
        <Button variant={'solid'} size={'lg'} data-selector="mfa-enter-code" onClick={onSubmit}>
          Enter code
        </Button>
      </MFAStep>

      <Stack mt={12}>
        <Button onClick={onChooseDifferent} variant={'secondary'} size={'lg'}>
          Use a different method
        </Button>
      </Stack>
    </>
  );
}
